import React from 'react';
import { Award, Target, Eye, Shield, Users, Briefcase, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { aboutContent, certification, companyInfo } from '../mock';

const About = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Tentang Kami</h1>
            <p className="text-xl text-blue-100">
              Perusahaan kontraktor profesional dengan standar kualitas internasional
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Team Image Section */}
            <div className="mb-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={aboutContent.teamImage} 
                    alt="Tim PT. Ids Sun Teknik"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">Tim Profesional Kami</h3>
                    <p className="text-blue-100">Berpengalaman & Bersertifikat</p>
                  </div>
                </div>
                <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={aboutContent.officeImage} 
                    alt="Kantor PT. Ids Sun Teknik"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">Kolaborasi Modern</h3>
                    <p className="text-blue-100">Teknologi & Inovasi Terkini</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Profil Perusahaan</h2>
                <p className="text-lg text-slate-700 leading-relaxed mb-6">
                  {aboutContent.overview}
                </p>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Kami melayani berbagai sektor termasuk industri, komersial, dan residensial dengan tim profesional yang berpengalaman dan berdedikasi tinggi.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Badge className="bg-blue-600 text-white px-4 py-2 text-sm">
                    <Award className="w-4 h-4 mr-2 inline" />
                    ISO {certification.standard}
                  </Badge>
                  <Badge className="bg-green-600 text-white px-4 py-2 text-sm">
                    <Shield className="w-4 h-4 mr-2 inline" />
                    Status {certification.status}
                  </Badge>
                </div>
              </div>
              
              <div>
                <Card className="bg-white border-2 border-blue-200 shadow-xl">
                  <CardContent className="p-8">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                        <Award className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-900">Sertifikasi ISO</h3>
                        <p className="text-slate-600">Standar Mutu Internasional</p>
                      </div>
                    </div>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between border-b border-slate-200 pb-2">
                        <span className="text-slate-600 font-medium">Nomor Sertifikat:</span>
                        <span className="text-slate-900 font-semibold">{certification.certificateNo}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-200 pb-2">
                        <span className="text-slate-600 font-medium">Referensi:</span>
                        <span className="text-slate-900 font-semibold">{certification.referenceNo}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-200 pb-2">
                        <span className="text-slate-600 font-medium">Standar:</span>
                        <span className="text-slate-900 font-semibold">{certification.standard}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-200 pb-2">
                        <span className="text-slate-600 font-medium">Status:</span>
                        <span className="text-green-600 font-bold">{certification.status}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-200 pb-2">
                        <span className="text-slate-600 font-medium">Provinsi:</span>
                        <span className="text-slate-900 font-semibold">{certification.state}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-200 pb-2">
                        <span className="text-slate-600 font-medium">Kota:</span>
                        <span className="text-slate-900 font-semibold">{certification.city}</span>
                      </div>
                      <div className="pt-2">
                        <p className="text-slate-700 font-medium mb-1">Ruang Lingkup:</p>
                        <p className="text-slate-600 text-sm leading-relaxed">{certification.scope}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="bg-gradient-to-br from-blue-50 to-slate-50 border-2 border-blue-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-blue-600 rounded-lg flex items-center justify-center mb-6">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Misi Kami</h3>
                  <p className="text-slate-700 leading-relaxed">
                    {aboutContent.mission}
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-slate-50 to-blue-50 border-2 border-slate-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-slate-700 rounded-lg flex items-center justify-center mb-6">
                    <Eye className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Visi Kami</h3>
                  <p className="text-slate-700 leading-relaxed">
                    {aboutContent.vision}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Nilai-Nilai Perusahaan</h2>
              <p className="text-lg text-slate-600">
                Prinsip yang menjadi fondasi dalam setiap pekerjaan kami
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {aboutContent.values.map((value, index) => (
                <Card key={index} className="bg-white border border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                        <CheckCircle className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2">{value.title}</h3>
                        <p className="text-slate-600 leading-relaxed">{value.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <div className="text-4xl font-bold mb-2">100+</div>
                <p className="text-blue-100">Klien Puas</p>
              </div>
              
              <div>
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-8 h-8 text-white" />
                </div>
                <div className="text-4xl font-bold mb-2">150+</div>
                <p className="text-blue-100">Proyek Selesai</p>
              </div>
              
              <div>
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <div className="text-4xl font-bold mb-2">10+</div>
                <p className="text-blue-100">Tahun Pengalaman</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
